/* eslint-disable no-console */
/**
 * FullStory session-lifecycle instrumentation utilities.
 *
 * All functions are no-ops when:
 *  - FullStory is disabled (window.FS not present)
 *  - Running server-side (no window)
 *  - sessionStorage is unavailable
 *
 * These events are designed to be compared across deployments in the
 * FullStory dashboard to measure the impact of performance improvements
 * (e.g. SSR, OIDC BFF) on real user load times.
 *
 * Timing strategy:
 *  - time_since_navigation_ms: performance.now() — accurate time from the
 *    browser's navigationStart for the current page load. Resets on redirects.
 *  - time_since_session_start_ms: Date.now() delta from the earliest client-side
 *    mark (bundle_evaluated). Survives SSO redirects via sessionStorage, giving
 *    true end-to-end load time including auth round-trips.
 *
 * Event timeline (in order of firing within a session):
 *
 *   bundle_evaluated       — JS bundle parsed and React tree initialising (ClientRoot mount)
 *   app_entry              — auth state known for the first time
 *   auth_redirect_started  — browser is about to leave for SSO (fired 0–N times)
 *   auth_redirect_returned — browser is back from SSO (fired 0–N times)
 *   user_ready             — identity resolved, org picker accessible
 *   org_ready              — org claim verified, user is inside an org
 *   page_interactive       — workspace content visible
 */

const SESSION_START_TS_KEY = 'fs_session_start_ts';
const APP_ENTRY_FIRED_KEY = 'fs_app_entry_fired';
const REDIRECT_COUNT_KEY = 'fs_auth_redirect_count';
const USER_READY_FIRED_KEY = 'fs_user_ready_fired';
const ORG_READY_TS_KEY = 'fs_org_ready_ts';
const ORG_READY_FIRED_KEY = 'fs_org_ready_fired';
const PAGE_INTERACTIVE_FIRED_KEY = 'fs_page_interactive_fired';
const INTERACTION_TIMING_PREFIX = 'fs_interaction_timing_';

type AuthProvider = 'auth0' | 'okta';

/**
 * Derives the auth provider from the build-time env var.
 * Mirrors the pattern used in CommonAuthProvider.tsx.
 */
function getAuthProvider(): AuthProvider {
  return process.env.AUTH_CLIENT_PROVIDER === 'auth0' ? 'auth0' : 'okta';
}

/**
 * Milliseconds since the browser's navigationStart for the current page load.
 * Uses the Performance API for sub-millisecond accuracy.
 */
export function getTimeSinceNavigationMs(): number {
  return Math.round(performance.now());
}

/**
 * Milliseconds since the session's earliest client-side mark (bundle_evaluated).
 * Survives SSO redirects via sessionStorage, so this captures total wall-clock
 * time including auth round-trips. Returns null before markBundleEvaluationTime
 * has fired (should not happen in practice).
 */
export function getSessionDeltaMs(): number | null {
  try {
    const ts = sessionStorage.getItem(SESSION_START_TS_KEY);

    if (!ts) {
      return null;
    }

    return Date.now() - Number(ts);
  } catch {
    return null;
  }
}

/**
 * Records the moment auth state is first resolved in this browser session.
 * Safe to call from multiple components (page.tsx + AuthGuard) — the
 * sessionStorage gate ensures the event fires exactly once.
 *
 * @param isAuthenticated - current auth state at the time of the call
 */
export function markAppEntry(isAuthenticated: boolean): void {
  try {
    if (sessionStorage.getItem(APP_ENTRY_FIRED_KEY)) {
      return;
    }

    sessionStorage.setItem(APP_ENTRY_FIRED_KEY, '1');

    window.FS?.event?.('app_entry', {
      auth_provider: getAuthProvider(),
      is_authenticated: isAuthenticated,
      time_since_navigation_ms: getTimeSinceNavigationMs(),
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark app entry, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment a redirect to an SSO provider is initiated.
 * Increments a redirect counter so app_ready can report the total
 * number of SSO round-trips in the session.
 *
 * Call this immediately before signinRedirect / loginWithRedirect.
 *
 * @param provider - 'auth0' or 'okta'
 * @param fromRoute - window.location.pathname at the moment of redirect
 */
export function markAuthRedirectStarted(provider: AuthProvider, fromRoute: string): void {
  try {
    const count = Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0') + 1;

    sessionStorage.setItem(REDIRECT_COUNT_KEY, String(count));

    window.FS?.event?.('auth_redirect_started', {
      auth_provider: provider,
      from_route: fromRoute,
      redirect_count: count,
    });
  } catch {
    console.warn('Failed to mark auth redirect returned, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the app receives control back from the SSO provider
 * (i.e. the /callback page has finished processing the token exchange).
 *
 * @param provider - 'auth0' or 'okta'
 * @param success - true if token exchange succeeded, false on error
 */
export function markAuthRedirectReturned(provider: AuthProvider, success: boolean): void {
  try {
    window.FS?.event?.('auth_redirect_returned', {
      auth_provider: provider,
      callback_route: window.location.pathname,
      success,
    });
  } catch {
    console.warn('Failed to mark auth redirect returned, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the user's identity is resolved:
 * auth complete + user data (memberships, profile) loaded.
 * The user is now able to choose an organisation.
 *
 * Fires at most once per browser session (sessionStorage gate).
 * In sessions where OrganizationAuthGuard does a re-login, this fires
 * before that redirect — use org_ready for the true end-to-end time.
 */
export function markUserReady(): void {
  try {
    if (sessionStorage.getItem(USER_READY_FIRED_KEY)) {
      return;
    }

    sessionStorage.setItem(USER_READY_FIRED_KEY, '1');

    window.FS?.event?.('user_ready', {
      auth_provider: getAuthProvider(),
      time_since_entry_ms: getTimeSinceNavigationMs(),
      time_since_session_start_ms: getSessionDeltaMs(),
      redirect_count: Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0'),
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark user ready, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the client-side JS bundle has been parsed and the React
 * tree is beginning to initialise. Fired as early as possible in ClientRoot —
 * the first client component in the tree.
 *
 * Also establishes the session start timestamp in sessionStorage — this is
 * the earliest client-side mark and anchors all time_since_session_start_ms
 * values. It persists across SSO redirects.
 */
export function markBundleEvaluationTime(): void {
  try {
    if (!sessionStorage.getItem(SESSION_START_TS_KEY)) {
      sessionStorage.setItem(SESSION_START_TS_KEY, String(Date.now() - performance.now()));
    }

    window.FS?.event?.('bundle_evaluated', {
      time_since_entry_ms: getTimeSinceNavigationMs(),
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark bundle evaluation time, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the user has fully landed in an organisation:
 * org claim verified, org context authorised, content rendered.
 *
 * Fires at most once per browser session (sessionStorage gate) —
 * subsequent org navigations within the same session are not tracked.
 * This is the primary KPI for end-to-end load time including any
 * org-scoped re-auth redirects triggered by OrganizationAuthGuard.
 *
 * @param organizationId - the org the user successfully landed in
 */
export function markOrgReady(organizationId: string): void {
  try {
    if (sessionStorage.getItem(ORG_READY_FIRED_KEY)) {
      return;
    }

    sessionStorage.setItem(ORG_READY_FIRED_KEY, '1');
    sessionStorage.setItem(ORG_READY_TS_KEY, String(Date.now()));

    window.FS?.event?.('org_ready', {
      auth_provider: getAuthProvider(),
      organization_id: organizationId,
      time_since_entry_ms: getTimeSinceNavigationMs(),
      time_since_session_start_ms: getSessionDeltaMs(),
      redirect_count: Number(sessionStorage.getItem(REDIRECT_COUNT_KEY) || '0'),
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark org ready, sessionStorage or FS unavailable');
  }
}

/**
 * Records the moment the workspace content is visible and interactive —
 * the loading state has resolved and real content is rendered.
 *
 * Fires at most once per browser session (sessionStorage gate).
 * The delta between org_ready and page_interactive isolates Next.js
 * rendering and data-fetching time from auth time. This is the metric
 * that will move when SSR / RSC / dependency optimisations are shipped,
 * independent of any auth flow improvements.
 */
export function markPageInteractive(): void {
  try {
    if (sessionStorage.getItem(PAGE_INTERACTIVE_FIRED_KEY)) {
      return;
    }

    sessionStorage.setItem(PAGE_INTERACTIVE_FIRED_KEY, '1');

    const orgReadyTs = sessionStorage.getItem(ORG_READY_TS_KEY);
    const timeSinceOrgReadyMs = orgReadyTs ? Date.now() - Number(orgReadyTs) : null;

    window.FS?.event?.('page_interactive', {
      auth_provider: getAuthProvider(),
      time_since_entry_ms: getTimeSinceNavigationMs(),
      time_since_session_start_ms: getSessionDeltaMs(),
      time_since_org_ready_ms: timeSinceOrgReadyMs,
      route: window.location.pathname,
    });
  } catch {
    console.warn('Failed to mark page interactive, sessionStorage or FS unavailable');
  }
}

// ---------------------------------------------------------------------------
// Login flow timing helpers
//
// These use a generic "mark start / mark end" pattern so the login components
// own when to start and stop the clock, keeping this file free of login-specific
// business logic.
// ---------------------------------------------------------------------------

interface TimerOptions {
  measureFromPageLoad?: boolean;
}

interface TimerEndOptions extends TimerOptions {
  extraProperties?: Record<string, unknown>;
  recordOncePerSession?: boolean;
}

/**
 * Starts a named timer by persisting an epoch timestamp to sessionStorage.
 * Survives page navigations and SSO redirects within the same tab.
 * Calling again with the same name overwrites the previous start time.
 *
 * @param measureFromPageLoad - when true, uses performance.timeOrigin (the epoch
 *   time of navigationStart) instead of Date.now(), so the timer starts from when
 *   the browser began navigating rather than when JS finished evaluating.
 */
export function markTimerStart(name: string, { measureFromPageLoad = false }: TimerOptions = {}): void {
  try {
    const startTime = measureFromPageLoad
      ? Math.round(performance.timeOrigin)
      : Date.now();

    sessionStorage.setItem(`${INTERACTION_TIMING_PREFIX}${name}`, String(startTime));
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * Completes a named timer and fires a FullStory event with the elapsed duration.
 * Returns the elapsed ms (or null if the start mark is missing or the event was
 * already recorded this session).
 * Clears the start mark after firing so the same timer can be reused.
 *
 * @param measureFromPageLoad - when true, uses performance.timeOrigin (the epoch
 *   time of navigationStart) as the end timestamp, so the elapsed duration measures
 *   up to when the browser began navigating to this page rather than when JS
 *   finished evaluating.
 * @param recordOncePerSession - when true, the event fires at most once per browser
 *   session (sessionStorage gate). Subsequent calls with the same name are no-ops.
 */
export function markTimerEnd(
  name: string,
  { extraProperties, measureFromPageLoad = false, recordOncePerSession = false }: TimerEndOptions = {},
): number | null {
  try {
    const firedKey = `${INTERACTION_TIMING_PREFIX}${name}_fired`;

    if (recordOncePerSession && sessionStorage.getItem(firedKey)) {
      return null;
    }

    const key = `${INTERACTION_TIMING_PREFIX}${name}`;
    const startTs = sessionStorage.getItem(key);

    if (!startTs) {
      return null;
    }

    const endTime = measureFromPageLoad
      ? Math.round(performance.timeOrigin)
      : Date.now();
    const elapsedMs = Math.max(0, endTime - Number(startTs));

    sessionStorage.removeItem(key);

    if (recordOncePerSession) {
      sessionStorage.setItem(firedKey, '1');
    }

    window.FS?.event?.(`interaction_timing_${name}`, {
      elapsed_ms: elapsedMs,
      time_since_entry_ms: getTimeSinceNavigationMs(),
      time_since_session_start_ms: getSessionDeltaMs(),
      ...extraProperties,
    });

    return elapsedMs;
  } catch {
    return null;
  }
}
